import { useMemo } from 'react';
import { ERoleNames } from '../../ducks/api-ducks/app-config-duck/types-app-config';
import { EQuotaAcknowledgementStatus, EUserRole } from '../../ducks/api-ducks/participation-details-duck/types-participation-details';
import { getLatestConfiguredParticipation } from '../../ducks/filters/utils-filters';
import { useAppSelector } from '../../ducks/hooks';
import { isNPM, useIsSinglePaylinePlan, useIsUBI } from '../../utils/participation-incentive-type-hook';
import { Flags } from '../feature-flags/constants-flags';
import { useFlagValue } from '../feature-flags/hooks';
import { useIsBusinessImpersonating } from '../../utils/impersonation-utils';

export const useParticipantSellerGuard = (ignoreRevenueRelease: boolean = false) => {
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const filters = useAppSelector((state) => state.filters);
  const isBusinessImpersonating = useIsBusinessImpersonating();
  return useMemo(() => {
    let show = false;

    // If participationDetails && filters states are not ready, then dont show.
    if (!(participationDetails && filters)) {
      return false;
    }

    const selectedParticipation = participationDetails.coreParticipations.find(
      (participation) =>
        filters.participationId === participation.participationId &&
        filters.selectedParticipationPartitionGroup?.partitionGroup === participation.fiscalYearId.toString()
    );

    // If the participation on the filters is not found on the details, then some bug happened. Dont show.
    if (!selectedParticipation) {
      return false;
    }

    // Do not show for NPM participations
    if (isNPM(selectedParticipation)) {
      return false;
    }

    // If its a business impersonation, then show regardless of plan status or RR status.
    // SPECIFICALLY REQUESTED ON 7/30/2025 by business team. https://microsoft.visualstudio.com/OSGS/_workitems/edit/58570847/?view=edit
    if (isBusinessImpersonating) {
      return true;
    }

    // If the selected participation is not active, then hide it
    if (!selectedParticipation.isPlanActive) {
      return false;
    }
    // If RevenueRelease is not enabled, then don't show.
    if (!ignoreRevenueRelease && !selectedParticipation.isRevenueReleaseEnabled) {
      return false;
    }

    // If the QA has not been accepted, then don't show.
    if (!ignoreRevenueRelease && selectedParticipation.quotaAcknowledgementStatus !== EQuotaAcknowledgementStatus.QuotaAccepted) {
      return false;
    }

    // If the selected participation userRole is ParticipantManager or Participant, then show.
    if (selectedParticipation.userRole === EUserRole.ParticipantManager || selectedParticipation.userRole === EUserRole.Participant) {
      show = true;
    } else {
      return false;
    }

    return show;
  }, [participationDetails, filters, isBusinessImpersonating, ignoreRevenueRelease]);
};

export const usePlanSummaryTabGuard = () => {
  const appConfig = useAppSelector((state) => state.appConfig);
  const impersonation = useAppSelector((state) => state.impersonation);
  const planSummaryTabFlag = useFlagValue(Flags.PLAN_SUMMARY_TAB) || (impersonation.isImpersonating && appConfig.isBusinessPersona);
  const participantSellerGuard = useParticipantSellerGuard(true);

  return useMemo(() => participantSellerGuard && !!planSummaryTabFlag, [planSummaryTabFlag, participantSellerGuard]);
};

export const useMyChangeHistoryTabGuard = () => {
  const myChangeHistoryTabFlag = useFlagValue(Flags.MY_CHANGE_HISTORY_TAB);
  const participantSellerGuard = useParticipantSellerGuard();
  const isUbi = useIsUBI();

  return useMemo(() => participantSellerGuard && !!myChangeHistoryTabFlag && !isUbi, [myChangeHistoryTabFlag, participantSellerGuard, isUbi]);
};

export const useEstimatorTabGuard = () => {
  const appConfig = useAppSelector((state) => state.appConfig);
  const impersonation = useAppSelector((state) => state.impersonation);
  const estimatorTabFlag = useFlagValue(Flags.IS_ESTIMATOR_ENABLED) || (impersonation.isImpersonating && appConfig.isBusinessPersona);
  const participantSellerGuard = useParticipantSellerGuard();

  return useMemo(() => participantSellerGuard && !!estimatorTabFlag, [estimatorTabFlag, participantSellerGuard]);
};

export const usePerformanceTabGuard = useParticipantSellerGuard;
export const useEarningsTabGuard = useParticipantSellerGuard;

// Helper hook that returns true if feature flag is enabled or user is a business admin and not viewing as another user (i.e. using Impersonation instead of ViewAs)
const useV2TabFeatureAccess = (flag: Flags) => {
  const appConfig = useAppSelector((state) => state.appConfig);
  const impersonation = useAppSelector((state) => state.impersonation);
  const flagEnabled = useFlagValue(flag);

  return useMemo(() => {
    const isBusinessAdmin = appConfig?.businessPersonaRoleName === ERoleNames.BusinessAdmin;
    const isViewAs = !!impersonation.impersonatedUser && !impersonation.isImpersonating;

    return !!flagEnabled || (isBusinessAdmin && !isViewAs);
  }, [appConfig?.businessPersonaRoleName, flagEnabled, impersonation.impersonatedUser, impersonation.isImpersonating]);
};

export const useMyCompensationStoryTabGuard = () => {
  const performanceTabGuard = usePerformanceTabGuard();
  const isSinglePaylinePlan = useIsSinglePaylinePlan();
  const isUbi = useIsUBI();
  const canAccessMyCompensation = useV2TabFeatureAccess(Flags.MY_COMPENSATION_STORY);

  return performanceTabGuard && !isSinglePaylinePlan && !isUbi && canAccessMyCompensation;
};

export const useMyPlanTabGuard = () => {
  const performanceTabGuard = usePerformanceTabGuard();
  const isUbi = useIsUBI();
  const canAccessMyPlan = useV2TabFeatureAccess(Flags.PRODUCT_FINDER);

  return performanceTabGuard && !isUbi && canAccessMyPlan;
};

export const useDataRefreshStatusPanelGuard = () => {
  const participantSellerGuard = useParticipantSellerGuard();
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const filters = useAppSelector((state) => state.filters);

  return useMemo(() => {
    if (!participantSellerGuard || !participationDetails || !filters) {
      return false;
    }

    const selectedParticipation = participationDetails.coreParticipations.find(
      (participation) =>
        filters.participationId === participation.participationId &&
        filters.selectedParticipationPartitionGroup?.partitionGroup === participation.fiscalYearId.toString()
    );

    if (!selectedParticipation) {
      return false;
    }

    return selectedParticipation.fiscalYearId >= 2027 && participantSellerGuard;
  }, [participantSellerGuard, participationDetails, filters]);
};

// Composes the data refresh status panel guard with the feature flag / business persona checks.
// Shared by the command bar button and any other entry point that opens the data refresh panel.
export const useShowDataRefreshStatusPanel = () => {
  const showDataRefreshStatus = useDataRefreshStatusPanelGuard();
  const dataRefreshStatusEnabled = useFlagValue(Flags.IS_DATA_REFRESH_STATUS_ENABLED);
  const isBusinessImpersonating = useIsBusinessImpersonating();

  return useMemo(
    () => showDataRefreshStatus && (dataRefreshStatusEnabled || isBusinessImpersonating),
    [showDataRefreshStatus, dataRefreshStatusEnabled, isBusinessImpersonating]
  );
};

// Hardcoded for now, this should be moved to a configuration on the API
export const useMyTeamTabGuard = () => {
  const appConfig = useAppSelector((state) => state.appConfig);
  const filters = useAppSelector((state) => state.filters);
  const impersonation = useAppSelector((state) => state.impersonation);
  const participationDetails = useAppSelector((state) => state.participationDetails);

  return useMemo(() => {
    let show = false;

    // If participationDetails && filters states are not ready, then dont show.
    if (!(participationDetails && filters)) {
      return false;
    }

    // To decide to show or not show MyTeamTab we only care about the latest configured participation userRole.
    // If a user is a manager today, he is able to see MyTeam tab for the past 3 years regardless of his previous role(s).
    const selectedParticipation = getLatestConfiguredParticipation(appConfig, participationDetails);
    const userNPM = isNPM(selectedParticipation);

    // If the participation on the filters is not found on the details, then some bug happened. Dont show.
    if (!selectedParticipation) {
      return false;
    }

    // If an impersonation is happening, and the participation is a manager, then show. (regardless of plan enabled/disabled or revenue release enabled/disabled)
    if (
      impersonation.isImpersonating &&
      (selectedParticipation.userRole === EUserRole.ParticipantManager || selectedParticipation.userRole === EUserRole.NonParticipantManager)
    ) {
      return true;
    }

    // If the selected participation is not active dont show, unless we are impersonating or the user is an NPM.
    if (!selectedParticipation.isPlanActive && !userNPM) {
      return false;
    }

    // If the selected participation userRole is ParticipantManager or NonParticipantManager, then show.
    if (selectedParticipation.userRole === EUserRole.ParticipantManager || selectedParticipation.userRole === EUserRole.NonParticipantManager) {
      show = true;
    } else {
      return false;
    }

    return show;
  }, [appConfig, filters, impersonation, participationDetails]);
};

export const useComplianceTabGuard = () => {
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const filters = useAppSelector((state) => state.filters);
  const isBusinessImpersonating = useIsBusinessImpersonating();

  return useMemo(() => {
    let show = false;

    // If participationDetails && filters states are not ready, then dont show.
    if (!(participationDetails && filters)) {
      return false;
    }

    const selectedParticipation = participationDetails.coreParticipations.find(
      (participation) => filters.participationId === participation.participationId
    );

    // If the participation on the filters is not found on the details, then some bug happened. Dont show.
    if (!selectedParticipation) {
      return false;
    }

    if (isNPM(selectedParticipation)) {
      return false;
    }

    // If plan is active, business is impersonating (irrespective of plan activation status), then show.
    if (isBusinessImpersonating) {
      return true;
    }

    // If the selected participation is not active, even if business is impersonating or the seller is, don't show.
    if (!selectedParticipation.isPlanActive) {
      return false;
    }

    // If the selected participation userRole is ParticipantManager or Participant, then show.
    if (selectedParticipation.userRole === EUserRole.ParticipantManager || selectedParticipation.userRole === EUserRole.Participant) {
      show = true;
    } else {
      return false;
    }

    return show;
  }, [participationDetails, filters, isBusinessImpersonating]);
};

export const useImpersonationTabGuard = () => {
  const appConfig = useAppSelector((state) => state.appConfig);
  const impersonation = useAppSelector((state) => state.impersonation);
  // const participationDetails = useAppSelector((state) => state.participationDetails);
  // const loggedUser = useAppSelector((state) => state.impersonation.loggedUser);

  return useMemo(() => {
    let show = false;

    // If appConfig is not ready yet, then don't show
    if (!appConfig) {
      return false;
    }
    // If appConfig.isBusinessPersona is true then show
    if (appConfig?.isBusinessPersona) {
      show = true;
    } else {
      return false;
    }

    // If no impersonation is happening, but there is an impersonated user it means is View as,
    // so don't show
    if (impersonation.impersonatedUser && !impersonation.isImpersonating) {
      return false;
    }

    // If there is participation details not owned by the logged in persona it means an impersonation (or view as) is happening, so no show
    // if (participationDetails && participationDetails.alias.toUpperCase() !== loggedUser.toUpperCase()) {
    //   return false;
    // }
    show = true;
    return show;
  }, [appConfig, impersonation.impersonatedUser, impersonation.isImpersonating]);
};

export const usePlanSetupTabGuard = () => {
  const appConfig = useAppSelector((state) => state.appConfig);
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const loggedUser = useAppSelector((state) => state.impersonation.loggedUser);
  return useMemo(() => {
    let show = false;

    // If appConfig is not ready yet, then don't show
    if (!appConfig) {
      return false;
    }
    // If appConfig.isBusinessPersona is true, then show
    if (appConfig?.businessPersonaRoleName === ERoleNames.BusinessAdmin) {
      show = true;
    } else {
      return false;
    }

    // If there is participation details not owned by the logged in persona it means an impersonation (or view as) is happening, so no show
    if (participationDetails && participationDetails.alias.toUpperCase() !== loggedUser.toUpperCase()) {
      return false;
    }
    show = true;
    return show;
  }, [appConfig, participationDetails, loggedUser]);
};

export const useNotificationsAdminTabGuard = () => {
  const appConfig = useAppSelector((state) => state.appConfig);
  const participationDetails = useAppSelector((state) => state.participationDetails);
  const loggedUser = useAppSelector((state) => state.impersonation.loggedUser);
  const notificationsadminTabFlag = useFlagValue(Flags.NOTIFICATIONS_ADMIN_TAB);

  return useMemo(() => {
    let show = false;

    // If appConfig is not ready yet, then don't show
    if (!appConfig) {
      return false;
    }

    // Verify if the feature flag is turne on
    if (!notificationsadminTabFlag) {
      return false;
    }
    // If appConfig.isBusinessPersona is true, then show
    if (appConfig?.businessPersonaRoleName === ERoleNames.BusinessAdmin) {
      show = true;
    } else {
      return false;
    }

    // If there is participation details not owned by the logged in persona it means an impersonation (or view as) is happening, so no show
    if (participationDetails && participationDetails.alias.toUpperCase() !== loggedUser.toUpperCase()) {
      return false;
    }
    show = true;
    return show;
  }, [appConfig, notificationsadminTabFlag, participationDetails, loggedUser]);
};
